import { useEffect, useState } from "react";
import ConsoleLayout from "@/components/ConsoleLayout";
import ResetPasswordModal from "@/components/ResetPasswordModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { teamsApi } from "@/lib/api";
import type { Team, TeamMember, WalletAndQuota } from "@/lib/types";
import { toast } from "sonner";
import {
  UserPlus,
  Users,
  X,
  Copy,
  Check,
  Wallet,
  ShieldCheck,
  Coins,
  KeyRound,
} from "lucide-react";
import { cn, formatSTAmount } from "@/lib/utils";

// Team admin's self-service page. Combines:
//   - Team info card (name, slug, wallet/quota snapshot)
//   - Member roster with status toggle + member invite
// Backed by /api/v1/teams/:tid/* endpoints. Member invite is fixed to
// role="member" here — promoting to team_admin is a sys_admin operation.
export default function TeamManagement() {
  const { t } = useLanguage();
  const { teamId, user: me } = useAuth();

  const [team, setTeam] = useState<Team | null>(null);
  const [wallet, setWallet] = useState<WalletAndQuota | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [resetTarget, setResetTarget] = useState<TeamMember | null>(null);

  const load = async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const [t, w, m] = await Promise.all([
        teamsApi.get(teamId),
        teamsApi.getWallet(teamId),
        teamsApi.listMembers(teamId, { includeInactive: true }),
      ]);
      setTeam(t);
      setWallet(w);
      setMembers(m.members);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const toggleStatus = async (m: TeamMember) => {
    if (!teamId) return;
    const next: "active" | "inactive" = m.status === "active" ? "inactive" : "active";
    setBusyId(m.id);
    try {
      await teamsApi.setMemberStatus(teamId, m.id, next);
      setMembers((prev) =>
        prev.map((x) => (x.id === m.id ? { ...x, status: next } : x))
      );
      toast.success(
        next === "active"
          ? `${m.display_name} ${t("teamMgmt.activated")}`
          : `${m.display_name} ${t("teamMgmt.deactivated")}`
      );
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Update failed");
    } finally {
      setBusyId(null);
    }
  };

  const activeCount = members.filter((m) => m.status === "active").length;
  const adminCount = members.filter(
    (m) => m.role === "team_admin" || m.is_system_admin
  ).length;

  return (
    <ConsoleLayout
      title={t("teamMgmt.title")}
      subtitle={t("teamMgmt.subtitle")}
      actions={
        <Button
          size="sm"
          className="btn-primary-sci gap-1.5 text-xs h-8"
          onClick={() => setInviteOpen(true)}
        >
          <UserPlus size={12} />
          {t("teamMgmt.invite")}
        </Button>
      }
    >
      <div className="p-6 space-y-5">
        {/* Team info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="sci-card p-4 lg:col-span-1">
            <div className="flex items-center gap-2 mb-2">
              <Users size={14} className="text-blue-400" />
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">
                {t("teamMgmt.teamInfo")}
              </span>
            </div>
            <div className="text-base font-semibold text-white truncate">
              {team?.name || "—"}
            </div>
            <div className="text-[10px] font-mono text-slate-500 truncate mt-0.5">
              {team?.slug || ""}
            </div>
            <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-2 gap-2 text-xs">
              <div>
                <div className="text-[10px] text-slate-500">
                  {t("teamMgmt.totalMembers")}
                </div>
                <div className="text-white font-mono text-sm">
                  {members.length}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-slate-500">
                  {t("teamMgmt.activeMembers")}
                </div>
                <div className="text-emerald-400 font-mono text-sm">
                  {activeCount}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-slate-500">
                  {t("teamMgmt.admins")}
                </div>
                <div className="text-blue-400 font-mono text-sm">
                  {adminCount}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-slate-500">
                  {t("teamMgmt.created")}
                </div>
                <div className="text-slate-300 font-mono text-xs">
                  {team
                    ? new Date(team.created_at).toLocaleDateString()
                    : "—"}
                </div>
              </div>
            </div>
          </div>

          <div className="sci-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <Wallet size={14} className="text-emerald-400" />
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">
                {t("teamMgmt.wallet")}
              </span>
            </div>
            <div className="text-2xl font-bold text-white font-mono">
              {wallet ? formatSTAmount(wallet.wallet.st_balance) : "—"}{" "}
              <span className="text-sm text-slate-500">ST</span>
            </div>
            <div className="mt-3 pt-3 border-t border-white/5 text-[10px] text-slate-500">
              {t("teamMgmt.walletHint")}
            </div>
          </div>

          <div className="sci-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <Coins size={14} className="text-amber-400" />
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">
                {t("teamMgmt.quota")}
              </span>
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">
                  {t("teamMgmt.memberLimit")}
                </span>
                <span className="font-mono text-slate-300">
                  {wallet?.quota.max_members != null
                    ? wallet.quota.max_members.toLocaleString()
                    : t("teamMgmt.unlimited")}
                </span>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-white/5 text-[10px] text-slate-500">
              {t("teamMgmt.quotaHint")}
            </div>
          </div>
        </div>

        {/* Member list */}
        <div className="sci-card overflow-hidden">
          <div className="px-5 py-3 border-b border-white/8 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users size={13} className="text-slate-400" />
              <h3
                className="text-sm font-semibold text-white"
                style={{ fontFamily: "Space Grotesk, sans-serif" }}
              >
                {t("teamMgmt.members")}
              </h3>
              <span className="text-xs text-slate-500">
                ({members.length}
                {wallet?.quota.max_members != null
                  ? ` / ${wallet.quota.max_members}`
                  : ""}
                )
              </span>
            </div>
          </div>
          <div className="px-5 py-2 border-b border-white/5 grid grid-cols-[2fr_1.2fr_100px_90px_200px] gap-3 text-[10px] text-slate-600 uppercase tracking-wider items-center">
            <div>{t("teamMgmt.colUser")}</div>
            <div>{t("teamMgmt.colEmail")}</div>
            <div>{t("teamMgmt.colRole")}</div>
            <div>{t("teamMgmt.colStatus")}</div>
            <div className="text-right">{t("teamMgmt.colActions")}</div>
          </div>
          {loading ? (
            <div className="px-5 py-10 text-center text-xs text-slate-500">
              Loading…
            </div>
          ) : members.length === 0 ? (
            <div className="px-5 py-10 text-center text-xs text-slate-500">
              {t("teamMgmt.noMembers")}
            </div>
          ) : (
            members.map((m) => {
              const isActive = m.status === "active";
              const isSelf = me?.id === m.id;
              const isAdmin = m.role === "team_admin" || m.is_system_admin;
              return (
                <div
                  key={m.id}
                  className={cn(
                    "px-5 py-3 border-b border-white/5 last:border-0 grid grid-cols-[2fr_1.2fr_100px_90px_200px] gap-3 items-center hover:bg-white/3 transition-colors",
                    !isActive && "opacity-60"
                  )}
                >
                  <div className="min-w-0 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">
                      {(m.display_name || m.email).charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate flex items-center gap-1.5">
                        {m.display_name || "—"}
                        {isSelf && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-500/15 text-slate-400 font-mono">
                            you
                          </span>
                        )}
                        {m.is_system_admin && (
                          <span
                            title="System Admin"
                            className="text-amber-400 flex-shrink-0"
                          >
                            <ShieldCheck size={11} />
                          </span>
                        )}
                      </div>
                      {m.last_login_at && (
                        <div className="text-[10px] text-slate-600">
                          {t("teamMgmt.lastLogin")}:{" "}
                          {new Date(m.last_login_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-slate-400 font-mono truncate">
                    {m.email}
                  </div>
                  <div>
                    <span
                      className={cn(
                        "text-[10px] px-2 py-0.5 rounded-full font-mono",
                        isAdmin
                          ? "bg-blue-500/15 text-blue-400"
                          : "bg-slate-500/15 text-slate-400"
                      )}
                    >
                      {m.role}
                    </span>
                  </div>
                  <div>
                    <span
                      className={cn(
                        "text-[10px] px-2 py-0.5 rounded-full font-mono",
                        isActive
                          ? "bg-emerald-500/15 text-emerald-400"
                          : "bg-slate-500/15 text-slate-500"
                      )}
                    >
                      {isActive ? "active" : "inactive"}
                    </span>
                  </div>
                  <div className="flex justify-end gap-1.5">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busyId === m.id || isSelf || !isActive}
                      onClick={() => setResetTarget(m)}
                      className="text-[10px] h-7 px-2.5 border border-white/10 text-slate-300 hover:text-white hover:bg-white/5 gap-1"
                      title={
                        isSelf
                          ? t("teamMgmt.cannotSelf")
                          : !isActive
                          ? t("teamMgmt.deactivated")
                          : t("teamMgmt.resetPwd")
                      }
                    >
                      <KeyRound size={11} />
                      {t("teamMgmt.resetPwd")}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busyId === m.id || isSelf || m.is_system_admin}
                      onClick={() => toggleStatus(m)}
                      className={cn(
                        "text-[10px] h-7 px-2.5 border border-white/10",
                        isActive
                          ? "text-amber-300 hover:text-amber-200 hover:bg-amber-500/10"
                          : "text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10"
                      )}
                      title={
                        isSelf
                          ? t("teamMgmt.cannotSelf")
                          : m.is_system_admin
                          ? t("teamMgmt.cannotSysadmin")
                          : undefined
                      }
                    >
                      {busyId === m.id
                        ? "..."
                        : isActive
                        ? t("teamMgmt.deactivate")
                        : t("teamMgmt.activate")}
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {me?.role === "team_admin" && team && (
          <div className="sci-card border-red-500/20 overflow-hidden mt-6">
            <div className="px-5 py-4 border-b border-red-500/20 bg-red-500/5">
              <h3 className="text-sm font-semibold text-red-400">危险区域 / Danger Zone</h3>
              <p className="text-xs text-red-400/80 mt-1">
                注销团队将永久停用团队、撤销所有 API Key，并核销团队余额。该操作不可撤销。
              </p>
            </div>
            <div className="px-5 py-4 flex items-center justify-between bg-black/20">
              <div>
                <div className="text-sm text-white font-medium">注销团队</div>
                <div className="text-xs text-slate-500 mt-1">彻底关闭 <strong>{team.name}</strong> 团队。</div>
              </div>
              <ArchiveTeamButton team={team} />
            </div>
          </div>
        )}
      </div>

      {inviteOpen && teamId && (
        <InviteModal
          teamId={teamId}
          onClose={() => setInviteOpen(false)}
          onInvited={() => {
            setInviteOpen(false);
            load();
          }}
        />
      )}

      {resetTarget && teamId && (
        <ResetPasswordModal
          teamId={teamId}
          member={resetTarget}
          onClose={() => setResetTarget(null)}
        />
      )}
    </ConsoleLayout>
  );
}

function InviteModal({
  teamId,
  onClose,
  onInvited,
}: {
  teamId: string;
  onClose: () => void;
  onInvited: () => void;
}) {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<{
    name: string;
    email: string;
    password: string;
  } | null>(null);
  const [pwdCopied, setPwdCopied] = useState(false);

  const submit = async () => {
    const e = email.trim();
    if (!e) {
      toast.error(t("teamMgmt.emailRequired"));
      return;
    }
    setBusy(true);
    try {
      const res = await teamsApi.inviteMember(teamId, {
        email: e,
        role: "member",
        display_name: displayName.trim() || undefined,
      });
      setCreated({
        name: res.user.display_name,
        email: res.user.email,
        password: res.initial_password,
      });
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      toast.error(
        msg === "email already registered"
          ? t("teamMgmt.emailExists")
          : msg || "Invite failed"
      );
    } finally {
      setBusy(false);
    }
  };

  const copyPwd = () => {
    if (!created) return;
    navigator.clipboard.writeText(created.password);
    setPwdCopied(true);
    setTimeout(() => setPwdCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={created ? onInvited : onClose}
      />
      <div className="relative w-full max-w-[480px] mx-4 sci-card p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2
            className="text-base font-semibold text-white"
            style={{ fontFamily: "Space Grotesk, sans-serif" }}
          >
            {t("teamMgmt.inviteTitle")}
          </h2>
          <button
            onClick={created ? onInvited : onClose}
            className="text-slate-500 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>

        {created ? (
          <>
            <div className="space-y-4">
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">
                {t("teamMgmt.pwdOnceWarn")}
              </div>
              <div className="space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 w-20">User</span>
                  <span className="text-white">{created.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 w-20">Email</span>
                  <span className="text-white font-mono">{created.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500 w-20">Password</span>
                  <code className="flex-1 px-2 py-1.5 rounded bg-black/40 border border-white/10 text-emerald-300 font-mono text-sm select-all">
                    {created.password}
                  </code>
                  <button
                    onClick={copyPwd}
                    title="Copy password"
                    className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5"
                  >
                    {pwdCopied ? (
                      <Check size={14} className="text-emerald-400" />
                    ) : (
                      <Copy size={14} />
                    )}
                  </button>
                </div>
                <p className="text-[10px] text-slate-500 pt-2">
                  {t("teamMgmt.mustChangePwd")}
                </p>
              </div>
            </div>
            <div className="flex gap-3 pt-5">
              <Button onClick={onInvited} className="flex-1 btn-primary-sci">
                {t("teamMgmt.done")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-xs text-slate-400 leading-relaxed mb-4">
              {t("teamMgmt.inviteHint")}
            </p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-400 mb-1.5 block">
                  {t("teamMgmt.emailLabel")}
                </Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="alice@example.com"
                  className="bg-white/5 border-white/10 text-white text-sm"
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-xs text-slate-400 mb-1.5 block">
                  {t("teamMgmt.displayNameLabel")}{" "}
                  <span className="text-slate-600">
                    {t("teamMgmt.optional")}
                  </span>
                </Label>
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Alice"
                  className="bg-white/5 border-white/10 text-white text-sm"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-5">
              <Button
                variant="ghost"
                onClick={onClose}
                className="flex-1 text-slate-400 hover:text-white border border-white/10"
              >
                {t("teamMgmt.cancel")}
              </Button>
              <Button
                onClick={submit}
                disabled={busy}
                className="flex-1 btn-primary-sci"
              >
                {busy ? "..." : t("teamMgmt.create")}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ArchiveTeamButton({ team }: { team: Team }) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const { logout } = useAuth();

  const submit = async () => {
    if (confirmText !== team.name) return;
    setBusy(true);
    try {
      await teamsApi.archiveTeam(team.id);
      toast.success("团队已注销");
      logout();
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === "team_has_active_members") {
        toast.error("仍有其他活跃成员,请先停用所有成员");
      } else if (code === "team_already_archived") {
        toast.error("团队已注销");
      } else if (err?.response?.status === 403) {
        toast.error("无权限");
      } else {
        toast.error(err?.response?.data?.message || "Failed to archive team");
      }
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <>
      <Button
        variant="destructive"
        className="bg-red-600 hover:bg-red-500 text-white"
        onClick={() => setOpen(true)}
      >
        注销团队
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <div className="relative w-full max-w-[480px] mx-4 sci-card p-6 border-red-500/30">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-semibold text-red-400">
                确认注销团队
              </h2>
              <button
                onClick={() => setOpen(false)}
                className="text-slate-500 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-300">
                <strong>警告：终止操作不可恢复</strong>
                <ul className="list-disc list-inside mt-2 ml-4 space-y-1">
                  <li>钱包余额将被核销</li>
                  <li>所有 API Key 将被吊销</li>
                  <li>您的账号将被停用并登出</li>
                </ul>
              </div>
              
              <div>
                <Label className="text-xs text-slate-400 mb-1.5 block">
                  请输入团队名称 <strong>{team.name}</strong> 以确认
                </Label>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={team.name}
                  className="bg-white/5 border-white/10 text-white text-sm"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex gap-3 pt-6">
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
                className="flex-1 text-slate-400 hover:text-white border border-white/10"
              >
                取消
              </Button>
              <Button
                onClick={submit}
                disabled={busy || confirmText !== team.name}
                className="flex-1 bg-red-600 hover:bg-red-500 text-white disabled:opacity-50"
              >
                {busy ? "处理中..." : "确认注销"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
